import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router';
import { Button, Chip, Stack, TextField, Typography } from '@mui/material';

import { eventExtended, personExtended, restDateToJsDate, restPersonName, type RestEvent, type RestPerson } from '../api/grampsRest';

type GeoReference = { lat: number; lon: number };

function normalizeSearch(input: string): string {
    return input.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase();
}

function parseRegexLiteral(input: string): { regex: RegExp | null; error: string | null } {
    const trimmed = input.trim();
    if (!trimmed.startsWith('/')) return { regex: null, error: null };
    const lastSlash = trimmed.lastIndexOf('/');
    if (lastSlash <= 0) return { regex: null, error: 'Regex invalide: utilisez le format /motif/flags' };
    try {
        return { regex: new RegExp(trimmed.slice(1, lastSlash), trimmed.slice(lastSlash + 1)), error: null };
    } catch {
        return { regex: null, error: 'Regex invalide' };
    }
}

function eventYear(event: RestEvent): number | undefined {
    if (typeof event.date?.year === 'number' && event.date.year > 0) return event.date.year;
    const year = event.date?.dateval?.[2];
    return typeof year === 'number' && year > 0 ? year : undefined;
}

function distanceKm(first: GeoReference, second: GeoReference): number {
    const radians = (value: number) => value * Math.PI / 180;
    const latitude = radians(second.lat - first.lat);
    const longitude = radians(second.lon - first.lon);
    const value = Math.sin(latitude / 2) ** 2 + Math.cos(radians(first.lat)) * Math.cos(radians(second.lat)) * Math.sin(longitude / 2) ** 2;
    return 6371 * 2 * Math.atan2(Math.sqrt(value), Math.sqrt(1 - value));
}

/**
 * Chronologically ordered, geocoded events for one person, oldest first.
 * Events without a resolvable date or without place coordinates can't take part
 * in an ordered "successive events" comparison, so they're dropped rather than
 * treated as a wildcard match.
 */
function chronologicalGeoEvents(events: RestEvent[]): Array<{ date: Date; point: GeoReference }> {
    return events
        .flatMap((event) => {
            const date = restDateToJsDate(event.date);
            const place = eventExtended(event).place;
            const lat = Number.parseFloat(place?.lat ?? '');
            const lon = Number.parseFloat(place?.long ?? '');
            return date && Number.isFinite(lat) && Number.isFinite(lon) ? [{ date, point: { lat, lon } }] : [];
        })
        .sort((a, b) => a.date.getTime() - b.date.getTime());
}

function maxSuccessiveDistanceKm(events: RestEvent[]): number {
    const points = chronologicalGeoEvents(events);
    let max = 0;
    for (let index = 1; index < points.length; index += 1) max = Math.max(max, distanceKm(points[index - 1].point, points[index].point));
    return max;
}

function activeFilters(searchParams: URLSearchParams) {
    return [['name', 'Nom'], ['occupation', 'Metier'], ['year', 'Annee'], ['migrationKm', 'Migration']].flatMap(([key, label]) => {
        const value = searchParams.get(`filters[${key}]`)?.trim();
        return value ? [{ key, label: key === 'migrationKm' ? `${label}: > ${value} km` : `${label}: ${value}` }] : [];
    });
}

export function RestPersonFilterActiveSummary() {
    const [searchParams] = useSearchParams();
    const filters = useMemo(() => activeFilters(searchParams), [searchParams]);
    return filters.length
        ? <Stack direction="row" spacing={0.75} useFlexGap sx={{ flexWrap: 'wrap' }}>{filters.map((filter) => <Chip key={filter.key} size="small" variant="outlined" label={filter.label} />)}</Stack>
        : <Typography variant="caption" color="text.secondary">Aucun filtre actif</Typography>;
}

export function RestPersonFilter({ persons, eventsByPerson, onFilterChange }: { persons: RestPerson[]; eventsByPerson: Map<string, RestEvent[]>; onFilterChange: (handles: Set<string>) => void }) {
    const [searchParams, setSearchParams] = useSearchParams();
    const lastInternalSearchParams = useRef<string | null>(null);
    const [name, setName] = useState(searchParams.get('filters[name]') ?? '');
    const [occupation, setOccupation] = useState(searchParams.get('filters[occupation]') ?? '');
    const [year, setYear] = useState(searchParams.get('filters[year]') ?? '');
    const [migrationDistance, setMigrationDistance] = useState(searchParams.get('filters[migrationKm]') ?? '');
    const regexState = useMemo(() => parseRegexLiteral(occupation), [occupation]);

    useEffect(() => {
        const serialized = searchParams.toString();
        if (lastInternalSearchParams.current === serialized) {
            lastInternalSearchParams.current = null;
            return;
        }
        setName(searchParams.get('filters[name]') ?? '');
        setOccupation(searchParams.get('filters[occupation]') ?? '');
        setYear(searchParams.get('filters[year]') ?? '');
        setMigrationDistance(searchParams.get('filters[migrationKm]') ?? '');
    }, [searchParams]);

    useEffect(() => {
        const timer = window.setTimeout(() => {
            const next = new URLSearchParams(window.location.search);
            const write = (key: string, value: string) => value.trim() ? next.set(`filters[${key}]`, value) : next.delete(`filters[${key}]`);
            write('name', name); write('occupation', occupation); write('year', year); write('migrationKm', migrationDistance);
            if (next.toString() !== searchParams.toString()) {
                lastInternalSearchParams.current = next.toString();
                setSearchParams(next, { replace: true });
            }
        }, 120);
        return () => window.clearTimeout(timer);
    }, [migrationDistance, name, occupation, searchParams, setSearchParams, year]);

    const matchingHandles = useMemo(() => {
        const normalizedName = normalizeSearch(name.trim());
        const normalizedOccupation = normalizeSearch(occupation.trim());
        const requestedRegex = occupation.trim().startsWith('/');
        const requestedYear = year.trim() ? Number(year) : undefined;
        const migrationKm = migrationDistance.trim() ? Number(migrationDistance) : undefined;
        const migrationActive = Number.isFinite(migrationKm) && migrationKm! >= 0;
        const hasFilter = !!normalizedName || !!normalizedOccupation || Number.isInteger(requestedYear) || migrationActive;
        if (!hasFilter) return [];
        return persons.filter((person) => {
            const events = person.handle ? eventsByPerson.get(person.handle) ?? [] : [];
            if (normalizedName && !normalizeSearch(restPersonName(person)).includes(normalizedName)) return false;
            if (normalizedOccupation) {
                const descriptions = events.filter((event) => event.type === 'Occupation').map((event) => event.description ?? '');
                const matches = regexState.regex ? descriptions.some((description) => regexState.regex!.test(description)) : !requestedRegex || !regexState.error ? descriptions.some((description) => normalizeSearch(description).includes(normalizedOccupation)) : false;
                if (!matches) return false;
            }
            if (Number.isInteger(requestedYear)) {
                const birth = eventYear((personExtended(person).events ?? []).find((event) => event.type === 'Birth') ?? {} as RestEvent);
                const death = eventYear((personExtended(person).events ?? []).find((event) => event.type === 'Death') ?? {} as RestEvent);
                if (!((birth ?? -Infinity) <= requestedYear! && requestedYear! <= (death ?? Infinity))) return false;
            }
            if (migrationActive && maxSuccessiveDistanceKm(events) <= migrationKm!) return false;
            return true;
        }).flatMap((person) => person.handle ? [person.handle] : []).sort();
    }, [eventsByPerson, migrationDistance, name, occupation, persons, regexState, year]);
    const hasAnyFilter = !!name.trim() || !!occupation.trim() || !!year.trim() || !!migrationDistance.trim();
    useEffect(() => {
        onFilterChange(new Set(matchingHandles));
    }, [matchingHandles, onFilterChange]);
    const clear = () => { setName(''); setOccupation(''); setYear(''); setMigrationDistance(''); };

    return <Stack spacing={2}>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField label="Nom" size="small" value={name} onChange={(event) => setName(event.target.value)} placeholder="Chercher par prénom ou nom" fullWidth />
            <TextField label="Année" type="number" size="small" value={year} onChange={(event) => setYear(event.target.value)} slotProps={{ htmlInput: { step: 1 } }} fullWidth />
            <TextField label="Métier" size="small" value={occupation} onChange={(event) => setOccupation(event.target.value)} placeholder="Texte ou /regex/flags" error={!!regexState.error} helperText={regexState.error ?? 'Exemple regex: /menuisier|charpentier/i'} fullWidth />
        </Stack>
        <Stack spacing={0.5}>
            <TextField label="Distance de migration (km)" type="number" size="small" value={migrationDistance} onChange={(event) => setMigrationDistance(event.target.value)} slotProps={{ htmlInput: { step: 1, min: 0 } }} sx={{ width: { xs: '100%', sm: 260 } }} />
            <Typography variant="caption" color="text.secondary">Personnes ayant au moins deux événements successifs (triés par date) séparés de plus de cette distance.</Typography>
        </Stack>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ alignItems: { xs: 'flex-start', sm: 'center' } }}><Typography variant="body2" color="text.secondary">{hasAnyFilter ? matchingHandles.length : persons.length} personne{(hasAnyFilter ? matchingHandles.length : persons.length) > 1 ? 's' : ''} correspondante{(hasAnyFilter ? matchingHandles.length : persons.length) > 1 ? 's' : ''}</Typography><Button variant="outlined" size="small" onClick={clear}>Effacer les filtres</Button></Stack>
    </Stack>;
}
